import React, {ReactElement} from "react";
import "./InfoBubble.css";
import {AuxiliaryToolTip, Icon} from "@spg/component_library";
import infoIcon from "../resources/icons/info.svg";
import {useIntl} from "react-intl";
import useWiki from "../hooks/useWiki";
import TooltipMap from "../lang/tooltips.json";

const tooltipMap = TooltipMap as unknown as Record<string, [string, string]>;

type BaseProps = {
  /** A prefix for a unique HTML-ID */
  idBase: string;
  /** Element that the tooltip attaches to */
  children: ReactElement;
  /** Additional props for the tool tip */
  auxiliaryToolTipProps?: Partial<
    Omit<React.ComponentProps<typeof AuxiliaryToolTip>, "toolTip" | "idBase">
  >;
};

type WikiProps = BaseProps & {
  /** ID that stores info about the location of the content in the wiki. Needs to be an entry in tooltips.json */
  tooltipId: string;
  /** Not available for wiki content - use tooltipId instead */
  text?: never;
};

type TextProps = BaseProps & {
  /** Static text content to display in the tooltip. */
  text: string;
  /** Not available for static text content - use text instead */
  tooltipId?: never;
};

type InfoBubbleProps = WikiProps | TextProps;

function useTooltipContent(tooltipId?: string, text?: string) {
  const tooltipInfo = tooltipId ? tooltipMap[tooltipId] : null;
  const wiki = tooltipInfo ? useWiki(...tooltipInfo) : null;

  if (text) return {content: text, isLoading: false};
  if (!wiki) return {content: null, isLoading: false};

  const isLoading = wiki.wikiContent?.unresolved;
  if (!wiki.tooltip && !isLoading) {
    console.error(
      "Info Content couldn't be fetched.",
      wiki.wikiContent?.errorReason,
    );
  }

  return {content: wiki.tooltip, isLoading};
}

/** A tooltip to wrap around a component which shows text fetched from the wiki. */
function InfoBubble({
  idBase,
  children,
  tooltipId,
  text,
  auxiliaryToolTipProps,
}: InfoBubbleProps) {
  const {formatMessage} = useIntl();
  const {content, isLoading} = useTooltipContent(tooltipId, text);

  const iconProps = {
    href: infoIcon + "#globalG",
    iconSize: {width: 16, height: 16},
    strokeColor: content ? "#101223" : "#c8c8c8",
    title: formatMessage({
      id: `infoBubble.icon.alt.${content ? "success" : isLoading ? "loading" : "error"}`,
    }),
  };

  if (!content) {
    return (
      <span className="infoBubble">
        {children}
        <Icon {...iconProps} />
      </span>
    );
  }

  return (
    <span className="infoBubble">
      <AuxiliaryToolTip
        toolTip={content}
        idBase={idBase + "InfoBubble"}
        tooltipPosition="right"
        toggleMessage={formatMessage({id: "infoBubble.actions.tooltipToggle"})}
        {...auxiliaryToolTipProps}
      >
        {children}
        <Icon {...iconProps} />
      </AuxiliaryToolTip>
    </span>
  );
}

export default InfoBubble;
